/**
 * Request Configuration and Interceptors
 * Handles request/response interceptor logic and configuration
 */
import { getLocale, history, matchPath } from 'umi';

import env from '@/config/env';
import { CoreEnums } from '@/utils/domain/constants';
import { getToken, isPublicFragment } from './tokenManager';

// Request context storage for interceptor communication
const requestContextMap = new Map();

const generateRequestId = () => Math.random().toString(36).substr(2, 9);

const storeRequestContext = (url, options) => {
  const requestId = generateRequestId();
  requestContextMap.set(requestId, { url, options });

  if (requestContextMap.size > 500) {
    const firstKey = requestContextMap.keys().next().value;
    requestContextMap.delete(firstKey);
  }

  return requestId;
};

const getRequestContext = (requestId) => requestContextMap.get(requestId);
const cleanupRequestContext = (requestId) => requestContextMap.delete(requestId);

const { ApplicationPage, ProcessInstanceRequestOrigin } = CoreEnums;

// Routes to omit from certain auth processing
const OMIT_ROUTES = [
  '/refreshToken',
  '/recoveryPassword',
  '/resetPassword',
  '/logout',
  '/confirmAccount',
];

const shouldOmitRoute = (url) => OMIT_ROUTES.every((route) => !url.includes(route));

const buildAuthHeader = (token, username, password) => {
  if (token) return `Bearer ${token}`;
  if (username) return `Basic ${btoa(`${username}:${password || ''}`)}`;
  return undefined;
};

const configureAuthRequest = (url, options, config) => {
  const { username, password, token, codeVerifier, code, ...restData } = options?.data || {};

  const authHeader = buildAuthHeader(token, username, password);
  const hasExplicitData = options?.data !== undefined && options?.data !== null;

  if (url.includes('/sendTwoFactorAuthCode') || url.includes('/validateCode')) {
    if (authHeader) {
      config.headers.Authorization = authHeader;
    } else if (hasExplicitData) {
      delete config.headers.Authorization;
    }
    config.data = options?.data;
    return;
  }

  if (authHeader) {
    config.headers.Authorization = authHeader;
  } else if (hasExplicitData) {
    delete config.headers.Authorization;
  }

  // Handle OAuth flows with additional data
  if (codeVerifier || code || Object.keys(restData).length > 0) {
    config.headers['Content-Type'] = 'application/json';
    config.data = {
      ...(codeVerifier && { codeVerifier }),
      ...(code && { code }),
      ...restData,
    };
  } else {
    delete config.data;
  }
};

const buildTwoFactorHeaders = (data) => {
  if (!data) return {};

  const getTwoFactorProvider = (provider) =>
    typeof provider === 'object' ? provider?.value : provider;

  const headers = {};

  if (data.twoFactorProvider) {
    headers.twoFactorProvider = getTwoFactorProvider(data.twoFactorProvider);
  }

  if (data.twoFactorCode) {
    headers.twoFactorCode = data.twoFactorCode;
  }

  return headers;
};

export const handleRedirectResponse = async (response) => {
  let responseData;

  try {
    responseData = await response.clone().json();
  } catch (e) {
    return;
  }

  const isEditForm = matchPath(history.location.pathname, {
    path: '/applications/continue',
    exact: false,
  });

  const redirectPaths = {
    [ApplicationPage.Home]: '/',
    [ApplicationPage.Tasks]: '/tasks',
    [ApplicationPage.Login]: '/user/login',
    [ApplicationPage.Processes]: '/processes',
    [ApplicationPage.Lists]: '/custom-lists',
    [ApplicationPage.ProcessInstanceDetails]:
      isEditForm && responseData?.processInstanceId
        ? `/applications/${responseData?.processInstanceId}`
        : null,
    [ApplicationPage.BulkRequests]: `/applications?requestOrigin=${ProcessInstanceRequestOrigin.BulkRequests}`,
  };

  const path = redirectPaths[responseData?.page];
  if (!path) return;

  // Skip the redirect when the user is already inside the target module
  // (e.g. on `/custom-lists/view?listId=...` and the response says
  // `page = Lists`). Forcing them back to the module root would drop the
  // deep-link they pasted - the page itself is responsible for showing
  // an empty/error state if its data could not be loaded.
  const currentPath = history.location.pathname || '';
  const targetBase = path.split('?')[0];
  const isAlreadyInTarget = currentPath === targetBase || currentPath.startsWith(`${targetBase}/`);
  if (isAlreadyInTarget) return;

  history.replace(path);
};

/**
 * Create request interceptor
 * @param {AbortController} abortController - Global abort controller
 * @returns {function} - Request interceptor function
 */
export const createRequestInterceptor = (abortController) => (url, options) => {
  const isValidUrl = !url.includes('[object Object]');
  if (!isValidUrl) abortController.abort();

  const isBlobChunkUpload =
    url.includes('automytemporary.blob.core.windows.net') &&
    options?.headers?.['Content-Type'] === 'application/octet-stream';

  if (
    isBlobChunkUpload ||
    options?.headers?.['x-ms-blob-type'] ||
    options?.headers?.['x-ms-blob-content-type']
  ) {
    return { url, options };
  }

  const locale = getLocale();
  const config = {
    ...options,
    headers: {
      ...options?.headers,
      clientId: env.CLIENT_ID,
      'Accept-Language': locale,
      'Strict-Transport-Security': 'max-age=7776000; includeSubDomains',
      ...(options?.readPriority && { 'AUTOMY-Read-Priority': 'primary' }),
    },
  };

  if (options?.trackingKey) {
    config.trackingKey = options.trackingKey;
    config.headers['x-tracking-key'] = options.trackingKey;
  }

  // Add two-factor headers for specific endpoints
  if (url.includes('/sendTwoFactorAuthCode') || url.includes('/validateCode')) {
    Object.assign(config.headers, buildTwoFactorHeaders(options?.data));
  }

  // Preserve original options for robust fallback in response interceptor
  // (survives even if requestContextMap evicts the entry)
  config._originalRequestOptions = options;

  if (
    url.includes('/auth') &&
    !url.includes('authorize') &&
    !url.includes('changePassword') &&
    !url.includes('logout') &&
    !url.includes('confirmAccount')
  ) {
    if (shouldOmitRoute(url)) {
      configureAuthRequest(url, options, config);
    }

    const requestId = storeRequestContext(url, options);
    config.headers['x-request-id'] = requestId;

    const finalUrl = url.startsWith('http') ? url : `${env.API_URL}${url}`;
    return { url: finalUrl, options: config };
  }

  const token = getToken();

  if (token) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${token.accessToken}`,
    };

    if (!options?.signal || options.signal.aborted) {
      const controller = new AbortController();
      config.signal = controller.signal;
    }
  } else if (!isPublicFragment()) {
    // Don't abort global controller for individual request failures
    const controller = new AbortController();
    controller.abort();
    config.signal = controller.signal;
  }

  const requestId = storeRequestContext(url, options);
  config.headers['x-request-id'] = requestId;

  const finalUrl = url.startsWith('http') ? url : `${env.API_URL}${url}`;
  return { url: finalUrl, options: config };
};

/**
 * Create response interceptor
 * @param {AbortController} abortController - Global abort controller
 * @returns {function} - Response interceptor function
 */
export const createResponseInterceptor = (abortController) => async (response, options) => {
  if (response.status >= 200 && (response.status < 300 || response.status === 301)) {
    window.dispatchEvent(new CustomEvent('retry-disabled', { detail: {} }));
  }

  // Extract request context - check response headers first, then fall back to request headers
  let requestId = response.headers?.get?.('x-request-id') || options?.headers?.['x-request-id'];
  let originalContext = requestId ? getRequestContext(requestId) : null;

  // Fallback: extract tracking key from headers
  let trackingKey = response.headers?.get?.('x-tracking-key') || options?.trackingKey;

  if (!originalContext && response.url) {
    // Prefer the preserved original options over the transformed ones
    const fallbackOptions = options?._originalRequestOptions || options || {};
    try {
      const urlObj = new URL(response.url);
      const extractedPath = urlObj.pathname;
      originalContext = { url: extractedPath, options: fallbackOptions, trackingKey };
    } catch {
      const urlMatch = response.url.match(/\/\/[^/]+([^?]*)/);
      const extractedPath = urlMatch ? urlMatch[1] : response.url;
      originalContext = { url: extractedPath, options: fallbackOptions, trackingKey };
    }
  }

  if (response.status === 206) {
    if (requestId) cleanupRequestContext(requestId);
    return response; // Handle SSO responses (206 status)
  }

  if (response.status >= 200 && response.status < 300) {
    if (requestId) cleanupRequestContext(requestId);
    return response;
  }

  if ([403, 401].includes(response.status)) {
    if (response.status === 403) {
      const { handleAuthError } = require('./authUtils');
      await handleAuthError(response, abortController);
    } else if (
      response.status === 401 &&
      !response.url?.includes('/refreshToken') &&
      !response.url?.includes('/auth/')
    ) {
      // Queue 401 requests for retry after token refresh
      // Auth endpoints (/auth/*) are excluded — they use Basic auth / credentials,
      // not Bearer tokens, so retrying after token refresh won't fix them.
      const { queueRequest } = await import('./requestQueue');

      let queueUrl = originalContext?.url;
      let queueOptions = {
        ...(originalContext?.options || {}),
        trackingKey: trackingKey || originalContext?.trackingKey,
      };

      // Fallback: extract path from response.url if context missing
      if (!queueUrl && response.url) {
        const url = new URL(response.url);
        queueUrl = url.pathname + url.search;
      }

      // Cleanup context before queueing
      if (requestId) cleanupRequestContext(requestId);

      // Return queueRequest promise - ensures DVA waits for retry completion
      return queueRequest(queueUrl || '/api/unknown', queueOptions, response);
    }
  }

  // Cleanup and throw error for error handler to process
  if (requestId) cleanupRequestContext(requestId);

  // Preserve tracking key in error
  const error = {
    response,
    request: {
      url: originalContext?.url,
      options: {
        ...(originalContext?.options || {}),
        trackingKey: trackingKey || originalContext?.trackingKey,
      },
    },
  };

  throw error;
};
